import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PRINT_LOGO_HEIGHT_MM,
  type PrintAlignment,
  type PrintableDocument,
  type PrintTemplate,
} from '@optiflow/types';
import { useCurrency } from '@/hooks/useCurrency';
import { useLocale } from '@/hooks/useLocale';
import { printLayout } from './printLayout';

/** Left and right follow the reading direction, so an Arabic document mirrors them. */
const ALIGN: Record<PrintAlignment, 'start' | 'center' | 'end'> = {
  LEFT: 'start',
  CENTER: 'center',
  RIGHT: 'end',
};

/**
 * The printed document itself, laid out by the tenant's print template.
 *
 * One renderer for every paper, every document type and the settings preview, because a receipt and an
 * invoice say the same things at different widths and a preview that drew them differently would lie.
 * The template decides only what is shown and where. The figures, number, date and QR are the
 * document's own; the grand total, number, date and QR with its caption always print, and a tax line
 * always prints when the document charged tax.
 */
export function PrintDocument({
  document: doc,
  template = doc.template,
}: {
  document: PrintableDocument;
  /** The settings preview passes its unsaved draft here; a real print always uses the document's own. */
  template?: PrintTemplate;
}) {
  const { t } = useTranslation();
  const { locale, direction } = useLocale();
  const { format: money } = useCurrency();
  const layout = printLayout(template);
  const thermal = layout.kind === 'thermal';

  const style = {
    width: `${layout.widthMm}mm`,
    maxWidth: '100%',
    fontSize: `${layout.fontPx}px`,
  } satisfies CSSProperties;

  const footer = locale === 'ar' ? template.footerAr : template.footerEn;
  const returnPolicy = locale === 'ar' ? template.returnPolicyAr : template.returnPolicyEn;
  const showTaxTotal = doc.taxMinor > 0 || template.showTaxTotal;
  const identity = doc.identity;
  const logoMm = PRINT_LOGO_HEIGHT_MM[layout.kind][template.logoSize];
  const header = [
    // The logo, legal name and tax number are the ones frozen with this document at issuance; a template only decides whether they print.
    template.showLogo && identity.logo ? (
      <img
        key="logo"
        className="print-logo"
        data-testid="print-logo"
        src={identity.logo.url}
        alt={t('sales.print.logoAlt')}
        style={{ height: `${logoMm}mm`, width: 'auto', maxWidth: '100%' }}
      />
    ) : null,
    template.showStoreName ? (
      <h1 key="store">
        <bdi>{doc.tenantName}</bdi>
      </h1>
    ) : null,
    template.showLegalName && identity.legalName ? (
      <p key="legal" data-testid="print-legal-name">
        <bdi>{identity.legalName}</bdi>
      </p>
    ) : null,
    template.showBranchName ? (
      <p key="branch">
        <bdi>{doc.branchName}</bdi>
      </p>
    ) : null,
    template.showBranchAddress && doc.branchAddress ? (
      <p key="address">
        <bdi>{doc.branchAddress}</bdi>
      </p>
    ) : null,
    template.showBranchPhone && doc.branchPhone ? (
      <p key="phone" dir="ltr">
        {doc.branchPhone}
      </p>
    ) : null,
    template.showTaxRegistration && identity.taxRegistrationNumber ? (
      <p key="tax" data-testid="print-tax-registration">
        {t('sales.print.taxRegistration')} <bdi dir="ltr">{identity.taxRegistrationNumber}</bdi>
      </p>
    ) : null,
  ].filter(Boolean);

  return (
    <article
      className={[
        'print-doc',
        `print-doc--${thermal ? 'thermal' : 'sheet'}`,
        `print-doc--${template.paperSize}`,
        `print-doc--${template.itemDensity.toLowerCase()}`,
      ].join(' ')}
      style={style}
      data-testid="print-document"
      data-paper={template.paperSize}
    >
      {header.length > 0 ? (
        <header className="print-head" style={{ textAlign: ALIGN[template.headerAlignment] }}>
          {header}
        </header>
      ) : null}

      <dl className="print-meta">
        <div>
          <dt>{t(`sales.print.${doc.kind === 'INVOICE' ? 'invoiceRef' : 'receiptRef'}`)}</dt>
          <dd dir="ltr">{doc.reference}</dd>
        </div>
        <div>
          <dt>{t('sales.print.date')}</dt>
          <dd dir="ltr">{doc.issuedAt}</dd>
        </div>
        {doc.customerName ? (
          <div>
            <dt>{t('sales.fields.customer')}</dt>
            <dd>{doc.customerName}</dd>
          </div>
        ) : null}
        {doc.cashierName ? (
          <div>
            <dt>{t('sales.print.cashier')}</dt>
            <dd>{doc.cashierName}</dd>
          </div>
        ) : null}
      </dl>

      {thermal ? (
        <ul className="print-lines" data-testid="print-lines">
          {doc.lines.map((line, index) => (
            <li key={`${line.sku ?? line.description}-${index}`}>
              <span className="print-line-name">{line.description}</span>
              {template.showSku && line.sku ? (
                <span className="print-sku" dir="ltr">
                  {line.sku}
                </span>
              ) : null}
              <span className="print-line-figures" dir="ltr">
                <span>
                  {template.showQuantity ? line.quantity : null}
                  {template.showQuantity && template.showUnitPrice ? ' × ' : null}
                  {template.showUnitPrice ? money(line.unitPriceMinor) : null}
                </span>
                {template.showLineTotal ? <strong>{money(line.totalMinor)}</strong> : null}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <table className="print-table" data-testid="print-table">
          <thead>
            <tr>
              <th>{t('sales.fields.description')}</th>
              {template.showQuantity ? <th>{t('sales.fields.quantity')}</th> : null}
              {template.showUnitPrice ? <th>{t('sales.fields.unitPrice')}</th> : null}
              {template.showDiscount ? <th>{t('sales.fields.discount')}</th> : null}
              {template.showTax ? <th>{t('sales.fields.taxShort')}</th> : null}
              {template.showLineTotal ? <th>{t('sales.fields.lineTotal')}</th> : null}
            </tr>
          </thead>
          <tbody>
            {doc.lines.map((line, index) => (
              <tr key={`${line.sku ?? line.description}-${index}`}>
                <td>
                  {line.description}
                  {template.showSku && line.sku ? (
                    <span className="print-sku" dir="ltr">
                      {line.sku}
                    </span>
                  ) : null}
                </td>
                {template.showQuantity ? <td dir="ltr">{line.quantity}</td> : null}
                {template.showUnitPrice ? <td dir="ltr">{money(line.unitPriceMinor)}</td> : null}
                {template.showDiscount ? <td dir="ltr">{money(line.discountMinor)}</td> : null}
                {template.showTax ? <td dir="ltr">{money(line.taxMinor)}</td> : null}
                {template.showLineTotal ? <td dir="ltr">{money(line.totalMinor)}</td> : null}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <dl className="print-totals" data-testid="print-totals">
        {template.showSubtotal ? (
          <div>
            <dt>{t('sales.fields.subtotal')}</dt>
            <dd dir="ltr">{money(doc.subtotalMinor)}</dd>
          </div>
        ) : null}
        {template.showDiscountTotal && doc.discountMinor > 0 ? (
          <div>
            <dt>{t('sales.fields.discount')}</dt>
            <dd dir="ltr">−{money(doc.discountMinor)}</dd>
          </div>
        ) : null}
        {showTaxTotal ? (
          <div>
            <dt>{t('sales.fields.taxShort')}</dt>
            <dd dir="ltr">{money(doc.taxMinor)}</dd>
          </div>
        ) : null}
        <div className="print-total" data-testid="print-grand-total">
          <dt>{t('sales.fields.total')}</dt>
          <dd dir="ltr">{money(doc.totalMinor)}</dd>
        </div>
      </dl>

      {template.showPaid && doc.payments.length > 0 ? (
        <dl className="print-totals">
          {doc.payments.map((payment, index) => (
            <div key={`${payment.method}-${index}`}>
              <dt>{t(`sales.methods.${payment.method}`)}</dt>
              <dd dir="ltr">{money(payment.amountMinor)}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {template.showOutstanding && doc.outstandingMinor > 0 ? (
        <p className="print-outstanding">
          {t('sales.print.outstanding')} <span dir="ltr">{money(doc.outstandingMinor)}</span>
        </p>
      ) : null}

      {/* The server draws the symbol; the template only sizes and places it, never smaller than a phone can read, and it always prints. */}
      <footer
        className="print-verify"
        data-testid="print-verify"
        style={{ textAlign: ALIGN[template.qrPlacement.replace('FOOTER_', '') as PrintAlignment] }}
      >
        <img
          className="print-verify__qr"
          src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(doc.verification.qrSvg)}`}
          alt={t('sales.print.qrLabel')}
          style={{ width: `${layout.qrMm}mm`, height: `${layout.qrMm}mm` }}
        />
        <p>{t('sales.print.qrHint')}</p>
      </footer>

      {template.showThankYou || footer || returnPolicy ? (
        <section className="print-footer" data-testid="print-footer" dir={direction}>
          {template.showThankYou ? <p>{t('sales.print.thankYou')}</p> : null}
          {footer ? <p className="print-footer__text">{footer}</p> : null}
          {returnPolicy ? <p className="print-footer__text">{returnPolicy}</p> : null}
        </section>
      ) : null}
    </article>
  );
}
